import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { 
  Package, 
  Users, 
  TrendingUp, 
  RefreshCw,
  Save,
  AlertCircle,
  Check,
  Loader2
} from "lucide-react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminSeasonalPostPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [foundingMembers, setFoundingMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [newSpotsClaimed, setNewSpotsClaimed] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [spotsRes, membersRes] = await Promise.all([
        axios.get(`${API_URL}/api/seasonal-post/founding-spots`),
        axios.get(`${API_URL}/api/seasonal-post/founding-members`)
      ]);
      setStats(spotsRes.data);
      setNewSpotsClaimed(spotsRes.data.spots_claimed);
      setFoundingMembers(membersRes.data.founding_members || []);
    } catch (err) {
      setError("Failed to load data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSpots = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      await axios.post(`${API_URL}/api/seasonal-post/admin/set-spots-claimed?spots_claimed=${newSpotsClaimed}`);
      
      setSuccess("Founding spots updated successfully!");
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update spots");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Are you sure you want to reset ALL founding spots to 0? This cannot be undone.")) {
      return;
    }
    
    try {
      setSaving(true);
      await axios.post(`${API_URL}/api/seasonal-post/admin/reset-founding-spots`);
      setSuccess("Founding spots reset to 50");
      await fetchData();
    } catch (err) {
      setError("Failed to reset spots");
    } finally {
      setSaving(false);
    }
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-600">Admin access required</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-lws-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 py-12 px-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-serif text-stone-800">The Seasonal Post</h1>
            <p className="text-stone-600">Manage founding spots and subscribers</p>
          </div>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 text-stone-600 hover:text-stone-800 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 text-green-700">
            <Check className="w-5 h-5" />
            {success}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-stone-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-amber-600" />
              </div>
              <span className="text-stone-600 font-medium">Total Spots</span>
            </div>
            <p className="text-4xl font-bold text-stone-800">{stats?.total_spots || 50}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-stone-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-stone-600 font-medium">Spots Claimed</span>
            </div>
            <p className="text-4xl font-bold text-green-600">{stats?.spots_claimed || 0}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-stone-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-stone-600 font-medium">Spots Remaining</span>
            </div>
            <p className="text-4xl font-bold text-blue-600">{stats?.spots_remaining || 50}</p>
          </motion.div>
        </div>

        {/* Manual Adjustment */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-stone-200 mb-8">
          <h2 className="text-xl font-semibold text-stone-800 mb-4">Adjust Founding Spots</h2>
          <p className="text-stone-600 mb-6">
            Manually adjust the number of founding spots claimed. Use this when a Shopify order 
            comes through or if you need to correct the count.
          </p>
          
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Spots Claimed
              </label>
              <input
                type="number"
                min="0"
                max={stats?.total_spots || 50}
                value={newSpotsClaimed}
                onChange={(e) => setNewSpotsClaimed(parseInt(e.target.value) || 0)}
                className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-lws-green/20 focus:border-lws-green"
              />
              <p className="text-sm text-stone-500 mt-1">
                This will show {(stats?.total_spots || 50) - newSpotsClaimed} spots remaining on the sales page
              </p>
            </div>
            <button
              onClick={handleUpdateSpots}
              disabled={saving || newSpotsClaimed === stats?.spots_claimed}
              className="flex items-center gap-2 px-6 py-3 bg-lws-green text-white rounded-lg hover:bg-lws-green/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Changes
            </button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-stone-200 mb-8">
          <h2 className="text-xl font-semibold text-stone-800 mb-4">Quick Actions</h2>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => { setNewSpotsClaimed(prev => Math.min(prev + 1, stats?.total_spots || 50)); }}
              className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
            >
              + Add 1 Spot Claimed
            </button>
            <button
              onClick={() => { setNewSpotsClaimed(prev => Math.max(prev - 1, 0)); }}
              className="px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors"
            >
              - Remove 1 Spot Claimed
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
            >
              Reset All (Danger)
            </button>
          </div>
        </div>

        {/* Founding Members List */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-stone-200">
          <h2 className="text-xl font-semibold text-stone-800 mb-4">
            Founding Correspondents Ledger
          </h2>
          {foundingMembers.length === 0 ? (
            <p className="text-stone-500 text-center py-8">
              No founding members yet. They will appear here when claimed via the API.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Spot #</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Claimed At</th>
                  </tr>
                </thead>
                <tbody>
                  {foundingMembers.map((member) => (
                    <tr key={member.spot_number} className="border-b border-stone-100">
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-amber-100 text-amber-700 rounded-full font-semibold">
                          {member.spot_number}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-medium text-stone-800">{member.name}</td>
                      <td className="py-3 px-4 text-stone-600">
                        {new Date(member.claimed_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-8 p-6 bg-amber-50 border border-amber-200 rounded-xl">
          <h3 className="font-semibold text-amber-800 mb-2">How to use this</h3>
          <ul className="text-sm text-amber-700 space-y-2">
            <li>• When a founding subscription is purchased via Shopify, manually increment the spots claimed here</li>
            <li>• The sales page will automatically show the updated count</li>
            <li>• Once 50 spots are claimed, the founding offer will show as "Sold Out"</li>
            <li>• For automated updates, set up a Shopify webhook to call the /api/seasonal-post/claim-founding-spot endpoint</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminSeasonalPostPage;
